import cn from 'classnames';
import { Field } from 'redux-form';
import connect from 'components/Shared/connect';
import { postAddressSuggestions, postZipcodeSuggestions } from 'services/ApiCheckoutPageService';
import { NO_ELEMENT, rm } from 'utils';
import * as rules from 'views/CheckoutPage/validation';
import { RenderLabeledInput, RenderCheckbox } from 'components/Shared/Form/inputs';
import {
  fieldStreet,
  fieldZipcode,
  selectDisableMessageBoxFeatureToggle
} from 'views/CheckoutPage/selector';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import ManualFormWrapper, { RenderFormProps } from '../manualForm';
import SuggestionField, { addressFormat, zipFormat, zipParser } from '../suggestionField';
import { LeaveMessage } from '../leaveMessage';
import { ToggleQuickstart } from '../toggleQuickstart';
import commonStyles from '../styles.scss';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';
interface OwnProps {
  toggleQuickStart: () => void;
}

interface MergedProps extends OwnProps {
  shouldDisableMessageBoxFeatureToggle: boolean;
}

const renderForm =
  ({ toggleQuickStart, shouldDisableMessageBoxFeatureToggle }: MergedProps) =>
  (props: RenderFormProps) => {
    const onAddressChange = (street: string) => {
      props.resetAddressValidation();
      return postAddressSuggestions(street);
    };

    const onZipCodeChange = (zipCode: string) => {
      props.resetAddressValidation();
      return postZipcodeSuggestions(zipCode);
    };

    return (
      <div>
        <ToggleQuickstart
          label={rm(
            'checkout_contact_info_address_automatic',
            rm('checkout_identityheader_company').toLowerCase()
          )}
          onClick={toggleQuickStart}
          className={commonStyles.toggleManual}
        />
        <Field
          name="lastName"
          validate={rules.name}
          className={commonStyles.input}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_companyname') }}
          onBlur={props.onBlur}
        />
        <SuggestionField
          name="street"
          className={commonStyles.input}
          validate={rules.address}
          label={rm('checkout_address')}
          onBlur={props.onBlur}
          onChange={onAddressChange}
          onSelect={props.suggestionSelected(fieldStreet)}
          format={addressFormat}
          props={{ customValid: props.hasValidAddress }}
        />
        <SuggestionField
          name="zipCode"
          className={cn(commonStyles.input, commonStyles.left)}
          validate={rules.zip}
          label={rm('checkout_zipcode')}
          onBlur={props.onBlur}
          onChange={onZipCodeChange}
          onSelect={props.suggestionSelected(fieldZipcode)}
          format={zipFormat}
          props={{
            inputMode: 'numeric',
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
          parse={zipParser}
        />
        <Field
          name="city"
          validate={rules.city}
          className={cn(commonStyles.input, commonStyles.right)}
          onBlur={props.onBlur}
          onChange={props.resetAddressValidation}
          component={RenderLabeledInput}
          props={{
            customLabel: rm('checkout_city'),
            customValid: props.hasValidAddress || props.hasValidZipCode
          }}
        />
        <Field
          name="hasDeliveryAddress"
          onChange={props.deliveryAddressChanged}
          component={RenderCheckbox}
          label={rm('checkout_delivery_alternative_label')}
          className={commonStyles.checkboxWrapper}
        />
        <Field
          name="reference"
          className={cn(commonStyles.input, commonStyles.left)}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_reference') }}
          onBlur={props.onBlur}
        />
        <Field
          name="littra"
          className={cn(commonStyles.input, commonStyles.right)}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_littra') }}
          onBlur={props.onBlur}
        />
        <Field
          name="phoneNumber"
          type="tel"
          tooltip={rm('checkout_cellphone_help')}
          validate={rules.phone}
          className={cn(commonStyles.input, commonStyles.left)}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_cellphone') }}
          onBlur={props.onBlur}
          inputMode="tel"
        />
        <Field
          name="email"
          type="email"
          validate={rules.email}
          className={cn(commonStyles.input, commonStyles.right)}
          component={RenderLabeledInput}
          props={{ customLabel: rm('checkout_email_rd3') }}
          onBlur={props.onBlur}
        />
        {shouldDisableMessageBoxFeatureToggle ? (
          NO_ELEMENT
        ) : (
          <LeaveMessage className={commonStyles.message} onBlur={props.onBlur} />
        )}
        <CertificateForm />
        <ContactInfoPolicy />
      </div>
    );
  };

const ManualForm = (props: MergedProps) => (
  <ManualFormWrapper formType="primary" renderForm={renderForm(props)} />
);

export default connect<MergedProps, OwnProps>(state => ({
  shouldDisableMessageBoxFeatureToggle: selectDisableMessageBoxFeatureToggle(state)
}))(ManualForm);
